import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Copy, 
  ExternalLink, 
  Link, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Pause,
  Play
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

interface PublicRegistrationUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
}

export function PublicRegistrationUrlDialog({ 
  open, 
  onOpenChange, 
  organizationId, 
  organizationName 
}: PublicRegistrationUrlDialogProps) {
  const { t, dir } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationPaused, setRegistrationPaused] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);

  const registrationUrl = `${window.location.origin}/new/${organizationId}`;

  // Load current pause status when dialog opens
  useEffect(() => {
    if (open && organizationId) {
      loadPauseStatus();
    }
  }, [open, organizationId]);

  const loadPauseStatus = async () => {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        setRegistrationPaused(orgData.registrationPaused || false);
      }
    } catch (error) {
      console.error('Error loading pause status:', error);
    }
  };

  const toggleRegistrationPause = async () => {
    try {
      setPauseLoading(true);
      const newPauseStatus = !registrationPaused;
      
      await updateDoc(doc(db, 'organizations', organizationId), {
        registrationPaused: newPauseStatus,
        updatedAt: new Date()
      });
      
      setRegistrationPaused(newPauseStatus);
      
      const message = newPauseStatus 
        ? (t('dashboard.manager.groups.publicRegistration.registrationPaused') || 'Registration paused')
        : (t('dashboard.manager.groups.publicRegistration.registrationResumed') || 'Registration resumed');
      
      toast.success(message);
    } catch (error) {
      console.error('Error toggling registration pause:', error);
      toast.error(t('dashboard.manager.groups.publicRegistration.toggleError') || 'Failed to update registration status');
    } finally {
      setPauseLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      setIsLoading(true);
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      toast.success(t('dashboard.manager.groups.publicRegistration.urlCopied') || 'تم نسخ الرابط!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(t('dashboard.manager.groups.publicRegistration.copyError') || 'فشل في نسخ الرابط');
    } finally {
      setIsLoading(false);
    }
  };

  const openUrl = () => {
    window.open(registrationUrl, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" dir={dir}>
        <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <DialogTitle className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse justify-end' : 'justify-start'}`}>
            <Link className="h-5 w-5" />
            {t('dashboard.manager.groups.publicRegistration.title') || 'رابط التسجيل العام'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('dashboard.manager.groups.publicRegistration.description') || 
               'شارك هذا الرابط مع المستفيدين المحتملين للسماح لهم بالتسجيل مباشرة في مؤسستك.'}
            </p>
            
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {t('dashboard.manager.groups.publicRegistration.organization') || 'المؤسسة'}: {organizationName}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={registrationUrl}
              readOnly
              className="font-mono text-sm"
              dir="ltr"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={copyToClipboard}
              disabled={isLoading}
              className="shrink-0"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : copied ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={openUrl}
              className="shrink-0"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>

          {/* Registration Pause Toggle */}
          <div className={`flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border ${registrationPaused ? 'border-amber-200' : 'border-gray-200'}`}>
            <div className="flex items-center gap-3">
              {registrationPaused ? (
                <Pause className="h-5 w-5 text-amber-600" />
              ) : (
                <Play className="h-5 w-5 text-green-600" />
              )}
              <div className="space-y-1">
                <Label htmlFor="pause-registration" className="text-sm font-medium">
                  {t('dashboard.manager.groups.publicRegistration.pauseTitle') || 'إيقاف التسجيل مؤقتاً'}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {registrationPaused 
                    ? (t('dashboard.manager.groups.publicRegistration.pauseDesc') || 'التسجيل متوقف حالياً - سيظهر للزوار رسالة إيقاف مؤقت')
                    : (t('dashboard.manager.groups.publicRegistration.activeDesc') || 'التسجيل نشط - يمكن للزوار التسجيل')
                  }
                </p>
              </div>
            </div>
            <Switch
              id="pause-registration"
              checked={registrationPaused}
              onCheckedChange={toggleRegistrationPause}
              disabled={pauseLoading}
            />
          </div>

          {/* Registration Status Badge */}
          <div className="flex items-center gap-2">
            <Badge 
              variant={registrationPaused ? "destructive" : "default"}
              className="text-xs"
            >
              {registrationPaused 
                ? (t('dashboard.manager.groups.publicRegistration.statusPaused') || 'متوقف مؤقتاً')
                : (t('dashboard.manager.groups.publicRegistration.statusActive') || 'نشط')
              }
            </Badge>
            {registrationPaused && (
              <span className="text-xs text-amber-600">
                {t('dashboard.manager.groups.publicRegistration.pausedMessage') || 'التسجيل في المنظمة متوقف مؤقتاً'}
              </span>
            )}
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">
                {t('dashboard.manager.groups.publicRegistration.info.title') || 'كيف يعمل:'}
              </p>
              <ul className="mt-1 space-y-1 list-disc list-inside">
                <li>
                  {t('dashboard.manager.groups.publicRegistration.info.step1') || 
                   'يزور المستخدمون الرابط ويملؤون نموذج التسجيل'}
                </li>
                <li>
                  {t('dashboard.manager.groups.publicRegistration.info.step2') || 
                   'يتم إنشاء حساب مستفيد جديد مع بريد إلكتروني تلقائي'}
                </li>
                <li>
                  {t('dashboard.manager.groups.publicRegistration.info.step3') || 
                   'يتم إضافة المستخدم تلقائياً إلى مؤسستك'}
                </li>
                <li>
                  {t('dashboard.manager.groups.publicRegistration.info.step4') || 
                   'يتلقى المستخدم بيانات تسجيل الدخول ويمكنه الوصول إلى المنصة'}
                </li>
              </ul>
            </div>
          </div>

          <div className={`flex justify-end gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.close') || 'إغلاق'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 